<!--
  @component
  Generates an SVG stacked bar chart and sets the color via an ordinal scale in `zScale`.
 -->
<script>
    import { getContext, createEventDispatcher } from "svelte";
    import { selectAll } from "d3-selection";

    const { data, yGet, xScale, yScale, height } = getContext("LayerCake");

    /** @type { import("../scripts/charts.js").PageContext } */
    export let ctx;
    /** @type {number} */
    export let lastYear;
    export let hideTooltip = true;
    // Assume mobile-screen to start; this gets overridden by bound window width
    let window_width = 375;
    $: onMouseEvents = window_width > 700; // tied to .interact-hide-mobile;

    const dispatch = createEventDispatcher();
    /**
     * @param {any} notice - in reality: Notice or null
     * @param {MouseEvent | FocusEvent} e
     */
    function handleMouseover(notice, e) {
        if (!onMouseEvents) return;
        if (notice) {
            selectAll(".rect").classed("no-hover", true);
            selectAll(`#rect-${notice.notice_id}`).classed("no-hover", false);
            hideTooltip = false;
            // When the element gets raised, it flashes 0,0 for a second so skip that
            dispatch("message", { e, props: notice });
        } else {
            selectAll(".rect").classed("no-hover", false);
            hideTooltip = true;
        }
    }
</script>

<svelte:window bind:innerWidth={window_width} />

<g class="map-group">
    {#each $data as d, i}
        <rect
            class="rect {d.completed_type}"
            class:rect-hover-enabled={onMouseEvents}
            id="rect-{d.notice_id}"
            data-id={i}
            x={$xScale(d.year)}
            y={$yGet(d)}
            width={$xScale(lastYear) - $xScale(d.year)}
            height={$height -
                $yScale(ctx["type"] == "tribe" ? d.number_mni : d.pct_complete)}
            on:mouseover={(e) => handleMouseover(d, e)}
            on:focus={(e) => handleMouseover(d, e)}
            on:mouseleave={(e) => handleMouseover(null, e)}
        />
    {/each}
</g>

<style lang="scss">
    .complete_dispositioned {
        fill: var(--dispositioned);
    }

    .complete,
    .complete_affiliated {
        fill: var(--affiliated);
        stroke: var(--affiliated);
    }

    .complete_transfer {
        fill: var(--transfer);
    }
    .complete_nca {
        fill: var(--nca);
        stroke: var(--nca);
    }
    .complete_reinter {
        fill: var(--reinter);
        stroke: var(--reinter);
    }

    rect {
        stroke-width: 1px;
        transition: stroke var(--transition), stroke-width var(--transition),
            fill-opacity var(--transition), stroke-opacity var(--transition);
    }

    rect:not(.rect-hover-enabled) {
        pointer-events: none;
    }

    .rect-hover-enabled:hover {
        stroke: var(--color-text-body);
        stroke-width: 1px;
    }

    rect.rect-hover-enabled.no-hover {
        fill-opacity: 0.2;
        stroke-opacity: 0;
        stroke-width: 0px;
    }

    .map-group:focus {
        outline: none;
    }
</style>
